import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, DollarSign, Activity, TrendingUp, TrendingDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const WOCostsModal = ({ onClose, wonum }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [costs, setCosts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!wonum) return;
    const fetchCosts = async () => {
      try {
        const res = await axios.get(`/api/workorder/${wonum}/costs`);
        setCosts(res.data);
      } catch (err) {
        console.error("Failed to fetch costs", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCosts();
  }, [wonum]);

  // دالة أمان لعرض الأرقام
  const formatNum = (num) => Number(num || 0).toLocaleString();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className={`bg-white w-full max-w-2xl rounded-lg shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300 ${isRTL ? 'text-right' : 'text-left'}`}>
        
        {/* Header */}
        <div className={`bg-[#1a3353] p-4 flex justify-between items-center text-white `}>
          <div className={`flex items-center gap-3 `}>
            <DollarSign className="text-green-400" />
            <h2 className="text-sm font-black uppercase tracking-widest">
              {t('wo.costs.title')} - {wonum}
            </h2>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 bg-slate-50 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 opacity-50">
              <Activity className="animate-spin mb-2" size={32} />
              <span className="text-xs font-black uppercase tracking-widest">{t('wo.costs.calculating')}</span>
            </div>
          ) : costs ? (
            <div className="space-y-6">
              
              <div className="grid grid-cols-2 gap-6">
                {/* Estimated Box */}
                <div className="bg-white p-4 border border-blue-100 rounded-lg shadow-sm">
                  <h3 className="text-xs font-black text-blue-900 uppercase border-b pb-2 mb-3">{t('wo.costs.estimated')}</h3>
                  <div className="space-y-2 text-sm font-bold text-gray-600">
                    <div className={`flex justify-between `}><span>{t('wo.costs.labor')}:</span> <span>{formatNum(costs.estimated.labor)}</span></div>
                    <div className={`flex justify-between `}><span>{t('wo.costs.materials')}:</span> <span>{formatNum(costs.estimated.materials)}</span></div>
                  </div>
                  <div className={`mt-4 pt-2 border-t flex justify-between font-black text-blue-900 text-lg `}>
                    <span>{t('wo.costs.total')}:</span> <span>{formatNum(costs.estimated.total)}</span>
                  </div>
                </div>

                {/* Actual Box */}
                <div className="bg-white p-4 border border-green-100 rounded-lg shadow-sm">
                  <h3 className="text-xs font-black text-green-700 uppercase border-b pb-2 mb-3">{t('wo.costs.actual')}</h3>
                  <div className="space-y-2 text-sm font-bold text-gray-600">
                    <div className={`flex justify-between `}><span>{t('wo.costs.labor')}:</span> <span>{formatNum(costs.actual.labor)}</span></div>
                    <div className={`flex justify-between `}><span>{t('wo.costs.materials')}:</span> <span>{formatNum(costs.actual.materials)}</span></div>
                    <div className={`flex justify-between `}><span>{t('wo.costs.tools')}:</span> <span>{formatNum(costs.actual.tools)}</span></div>
                    <div className={`flex justify-between `}><span>{t('wo.costs.services')}:</span> <span>{formatNum(costs.actual.services)}</span></div>
                  </div>
                  <div className={`mt-4 pt-2 border-t flex justify-between font-black text-green-700 text-lg `}>
                    <span>{t('wo.costs.total')}:</span> <span>{formatNum(costs.actual.total)}</span>
                  </div>
                </div>
              </div>

              {/* Variance Alert */}
              <div className={`p-4 rounded-lg flex items-center justify-between border-l-4 ${isRTL ? 'border-r-4 border-l-0' : ''} ${costs.variance > 0 ? 'bg-red-50 border-red-500' : 'bg-green-50 border-green-500'} `}>
                <div className={`flex items-center gap-3 `}>
                  {costs.variance > 0 ? <TrendingUp className="text-red-500" /> : <TrendingDown className="text-green-500" />}
                  <span className="font-black text-sm uppercase">{t('wo.costs.variance')}</span>
                </div>
                <span className={`text-xl font-black ${costs.variance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {costs.variance > 0 ? '+' : ''}{formatNum(costs.variance)}
                </span>
              </div>

            </div>
          ) : (
            <div className="text-center py-10 text-gray-400 font-bold italic">{t('wo.costs.no_data')}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WOCostsModal;